import {
  ExponentialBackoff,
  retry,
  handleAll,
  noJitterGenerator,
} from 'cockatiel'

import type { LoggerCollector } from './LoggerCollector'
import { minutes } from '../utils/time/duration'
import type { PinoLog } from './pino/PinoLog'
import { isPinoLog } from './pino/isPinoLog'

export type LoggerOrchestratorArgs = {
  collector: LoggerCollector
  onDrain?: () => void
}

export type LoggerOrchestrator = ReturnType<typeof LoggerOrchestrator.create>

export type LoggerOrchestratorState =
  | 'idle'
  | 'scheduled'
  | 'running'
  | 'stopped'

export const LoggerOrchestrator = {
  create({ onDrain, collector }: LoggerOrchestratorArgs) {
    let state: LoggerOrchestratorState = 'idle'

    // TODO: what we should do if the collector is down and the events keep growing?
    let events: PinoLog[] = []

    const schedulePolicy = retry(handleAll, {
      maxAttempts: Infinity,
      backoff: new ExponentialBackoff({
        maxDelay: minutes(3),
        ...(process.env.NODE_ENV === 'test'
          ? {
              generator: noJitterGenerator,
            }
          : {}),
      }),
    })

    let scheduleId: NodeJS.Timeout | null = null
    let controller: AbortController | null = null

    const scheduleNotificationToCollector = () => {
      if (events.length === 0 || state !== 'idle') return

      state = 'scheduled'

      scheduleId = setTimeout(async () => {
        state = 'running'

        const eventsOnNotification = events.splice(0)

        controller = new AbortController()
        controller.signal.addEventListener('abort', () => {
          events = [...eventsOnNotification, ...events]
        })

        await schedulePolicy.execute(async ({ signal }) => {
          try {
            await collector.send(eventsOnNotification, { signal })
          } catch (err) {
            if (signal.aborted) return

            throw err
          }

          if (signal.aborted) return

          onDrain?.()
          state = 'idle'
          scheduleId = null
          controller = null

          scheduleNotificationToCollector()
        }, controller.signal)
      }, 20)
    }

    const stopScheduledNotification = () => {
      if (scheduleId) {
        clearTimeout(scheduleId)
        scheduleId = null
      }

      if (controller) {
        controller.abort()
        controller = null
      }
    }

    const orchestrator = {
      send(event: unknown) {
        if (!isPinoLog(event)) {
          throw new Error('Event must be a PinoLog')
        }

        events.push(event)

        scheduleNotificationToCollector()
      },

      resume() {
        state = 'idle'
        scheduleNotificationToCollector()
      },

      pause() {
        state = 'stopped'
        stopScheduledNotification()
      },
    }

    return orchestrator
  },
}
